/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { Alert, Box, Divider, LinearProgress, Stack, Typography } from '@mui/material';
import TableService from '../../../../services/TableService';
import * as selectorsAuth from '../../../../store/auth/reducer';
import * as actionsNsir from '../../../../store/nsir/actions';
import * as Role from '../../../../constants/role';
import { normalizeExportFilterForApi } from './constants';
import HeroStrip from './HeroStrip';
import TipoServicoSegment from './TipoServicoSegment';
import RitmoHero from './RitmoHero';
import AderePeriodoCard from './AderePeriodoCard';
import PeriodoSectionHeader from './PeriodoSectionHeader';
import ErroIntegracaoBanner from '../Operador/ErroIntegracaoBanner';
import FilaKpis from '../Operador/FilaKpis';
import NsirsAtivasCards from '../Operador/NsirsAtivasCards';
import HistoricoPeriodoTable from '../Operador/HistoricoPeriodoTable';
import ErrosIntegracaoQuickDrawer from '../Operador/ErrosIntegracaoQuickDrawer';
import MediaDiariaQuickModal from '../Operador/MediaDiariaQuickModal';
import DiasUteisCicloQuickModal from './DiasUteisCicloQuickModal';
import OperadorFetchErrorAlert from '../Operador/OperadorFetchErrorAlert';
import { WidgetV2 } from '../../../../components/v2';
import ExportFilterDialog from './ExportFilterDialog';
import {
  buildAdereFilter,
  buildHistoricoFilter,
  buildResumoFilter,
  EMPTY_ADERE,
  EMPTY_LENTES,
  EMPTY_RESUMO_NOVA,
  formatPeriodoCurto,
  getCurrentAdereMonth,
  getDefaultFilterDigitador,
  getHistoricoEmptyMessage,
  getMedicaoAtualPeriod,
  isLenteMtBt,
  KPI_KEYS,
  normalizeAdereResponse,
  normalizeLentesResponse,
  normalizeMinhaFilaFilterForApi,
  normalizeMinhaFilaResponse,
  normalizeResumoNova,
  resolveAdereMonthsForUi,
  resolveDefaultAdereMonth,
} from './constants';
import { EMPTY_MINHA_FILA } from '../Operador/constants';

const MinhaDigitacaoV2 = ({ authData, generateReportDigitadorDiario }) => {
  const medicaoPeriod = useMemo(() => getMedicaoAtualPeriod(), []);
  const medicaoFilter = useMemo(
    () => ({
      ...getDefaultFilterDigitador(authData),
      inicio: medicaoPeriod.inicio,
      fim: medicaoPeriod.fim,
    }),
    [authData, medicaoPeriod],
  );

  const [reload, setReload] = useState(false);
  const [lentesData, setLentesData] = useState(EMPTY_LENTES);
  const [lenteId, setLenteId] = useState(null);
  const [adereMonth, setAdereMonth] = useState(null);
  const [adere, setAdere] = useState(EMPTY_ADERE);
  const [resumo, setResumo] = useState(EMPTY_RESUMO_NOVA);
  const [heroResumos, setHeroResumos] = useState({});
  const [minhaFila, setMinhaFila] = useState(EMPTY_MINHA_FILA);
  const [lentesLoading, setLentesLoading] = useState(false);
  const [resumoLoading, setResumoLoading] = useState(false);
  const [adereLoading, setAdereLoading] = useState(false);
  const [filaLoading, setFilaLoading] = useState(false);
  const [lentesError, setLentesError] = useState(false);
  const [lentesPendingDeploy, setLentesPendingDeploy] = useState(false);
  const [resumoError, setResumoError] = useState(false);
  const [filaError, setFilaError] = useState(false);
  const [openErrosDrawer, setOpenErrosDrawer] = useState(false);
  const [openMediaModal, setOpenMediaModal] = useState(false);
  const [openDiasUteisModal, setOpenDiasUteisModal] = useState(false);
  const [openExportFilter, setOpenExportFilter] = useState(false);

  const activeLente = useMemo(
    () => lentesData.lentes.find((l) => l.id === lenteId) ?? null,
    [lentesData.lentes, lenteId],
  );

  /** Filtro do período (KPIs / ritmo / erros) — sem adere. */
  const periodoApiFilter = useMemo(
    () => buildResumoFilter(medicaoFilter, authData, lenteId),
    [medicaoFilter, authData, lenteId],
  );

  const historicoApiFilter = useMemo(
    () => buildHistoricoFilter(medicaoFilter, authData, lenteId),
    [medicaoFilter, authData, lenteId],
  );

  const periodoFetchReady = !lentesLoading && (
    lenteId != null
    || lentesPendingDeploy
    || lentesError
    || lentesData.lentes.length === 0
  );

  const filaApiFilter = useMemo(
    () => normalizeMinhaFilaFilterForApi(medicaoFilter, authData),
    [medicaoFilter, authData],
  );

  const fetchLentes = useCallback(async () => {
    setLentesLoading(true);
    setLentesError(false);
    setLentesPendingDeploy(false);
    try {
      const response = await TableService.request('reports/digitacao/me/lentes', { meses: 2 });
      const normalized = normalizeLentesResponse(response?.data);
      setLentesData(normalized);
      setLenteId((prev) => prev ?? normalized.defaultLenteId ?? null);
    } catch (error) {
      setLentesData({ ...EMPTY_LENTES });
      setLentesError(true);
      if (error?.response?.status === 404) {
        setLentesPendingDeploy(true);
      }
    } finally {
      setLentesLoading(false);
    }
  }, [reload]);

  const fetchResumo = useCallback(async () => {
    if (!periodoFetchReady) return;
    setResumoLoading(true);
    setResumoError(false);
    try {
      const response = await TableService.request('reports/digitacao/me/resumo', periodoApiFilter);
      setResumo(normalizeResumoNova(response?.data));
    } catch {
      setResumo(EMPTY_RESUMO_NOVA);
      setResumoError(true);
    } finally {
      setResumoLoading(false);
    }
  }, [periodoApiFilter, reload, periodoFetchReady]);

  /** Adere dedicado — independente do ritmo do ciclo (`/me/resumo`). */
  const fetchAdere = useCallback(async (month) => {
    if (!periodoFetchReady || !isLenteMtBt(lenteId)) {
      setAdere({ ...EMPTY_ADERE });
      setAdereMonth(null);
      return;
    }
    const lenteMtBt = lentesData.lentes.find((l) => l.id === lenteId) ?? null;
    const monthToFetch = month || getCurrentAdereMonth();
    setAdereLoading(true);
    try {
      const filter = buildAdereFilter(authData, lenteMtBt, monthToFetch);
      const response = await TableService.request('reports/digitacao/me/adere', filter);
      const normalized = normalizeAdereResponse(response?.data);
      setAdere(normalized);
      const months = resolveAdereMonthsForUi(
        normalized,
        medicaoPeriod.inicio,
        medicaoPeriod.fim,
      );
      const nextMonth = months.includes(monthToFetch)
        ? monthToFetch
        : resolveDefaultAdereMonth(normalized, months);
      setAdereMonth(nextMonth);
    } catch {
      setAdere({ ...EMPTY_ADERE });
      if (!month) setAdereMonth(null);
    } finally {
      setAdereLoading(false);
    }
  }, [periodoFetchReady, lenteId, lentesData.lentes, authData, medicaoPeriod, reload]);

  const fetchHeroResumos = useCallback(async () => {
    if (lentesData.lentes.length < 2) {
      setHeroResumos({});
      return;
    }
    const baseFilter = buildResumoFilter(medicaoFilter, authData, null);
    try {
      const results = await Promise.all(
        lentesData.lentes.map(async (lente) => {
          const filter = { ...baseFilter, lenteId: lente.id };
          const response = await TableService.request('reports/digitacao/me/resumo', filter);
          return [lente.id, normalizeResumoNova(response?.data)];
        }),
      );
      setHeroResumos(Object.fromEntries(results));
    } catch {
      setHeroResumos({});
    }
  }, [lentesData.lentes, medicaoFilter, authData, reload]);

  const fetchMinhaFila = useCallback(async () => {
    setFilaLoading(true);
    setFilaError(false);
    try {
      const response = await TableService.request('reports/digitacao/me/fila', filaApiFilter);
      setMinhaFila(normalizeMinhaFilaResponse(response?.data));
    } catch {
      setMinhaFila(EMPTY_MINHA_FILA);
      setFilaError(true);
    } finally {
      setFilaLoading(false);
    }
  }, [filaApiFilter, reload]);

  useEffect(() => {
    fetchLentes();
    fetchMinhaFila();
  }, [fetchLentes, fetchMinhaFila]);

  useEffect(() => {
    fetchResumo();
  }, [fetchResumo]);

  useEffect(() => {
    fetchAdere(null);
  }, [fetchAdere]);

  useEffect(() => {
    fetchHeroResumos();
  }, [fetchHeroResumos]);

  const onReload = () => setReload((prev) => !prev);

  const handleExport = () => setOpenExportFilter(true);

  const handleConfirmExport = (filter) => {
    generateReportDigitadorDiario(normalizeExportFilterForApi(filter, authData));
  };

  const handleLenteChange = (nextLenteId) => {
    setLenteId(nextLenteId);
    setAdereMonth(null);
    setAdere({ ...EMPTY_ADERE });
  };

  const handleAdereMonthChange = (mes) => {
    if (!mes || mes === adereMonth) return;
    setAdereMonth(mes);
    fetchAdere(mes);
  };

  const handleKpiClick = (key) => {
    if (key === KPI_KEYS.MEDIA) {
      setOpenMediaModal(true);
    } else if (key === KPI_KEYS.DIAS_UTEIS) {
      setOpenDiasUteisModal(true);
    } else if (key === KPI_KEYS.ERROS) {
      setOpenErrosDrawer(true);
    }
  };

  const historicoSubheaderLente = activeLente?.label ?? 'digitação';
  const { inicio, fim } = medicaoPeriod;

  if (authData?.role !== Role.DIGITADOR) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Este painel é destinado a digitadores. Use o relatório administrativo em Relatórios → Digitadores.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        <HeroStrip
          authData={authData}
          inicio={inicio}
          fim={fim}
          lentes={lentesData.lentes}
          heroResumos={heroResumos}
          loading={lentesLoading || resumoLoading}
          onExport={handleExport}
        />

        <Stack spacing={2}>
          {filaError && (
            <OperadorFetchErrorAlert
              message="Não foi possível carregar sua fila de NSIRs."
              onReload={onReload}
            />
          )}

          <FilaKpis minhaFila={minhaFila} loading={filaLoading} />
          <NsirsAtivasCards itens={minhaFila.itens} loading={filaLoading} />
        </Stack>

        <Divider />

        <Stack spacing={2}>
          {lentesPendingDeploy && (
            <Alert severity="info">
              Segmentação por tipo de serviço (`/me/lentes`) ainda não disponível no servidor.
              Métricas do período usam o contrato base até o deploy.
            </Alert>
          )}

          {lentesError && !lentesPendingDeploy && (
            <OperadorFetchErrorAlert
              message="Não foi possível carregar seus tipos de serviço."
              onReload={onReload}
            />
          )}

          {!lentesLoading && !lentesError && lentesData.lentes.length === 0 && (
            <Alert severity="warning">
              Sem digitação nos últimos 2 meses — nenhum segmento disponível.
            </Alert>
          )}

          {lentesData.lentes.length > 1 && (
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1, display: 'block', mb: 0.5 }}
              >
                Tipo de serviço
              </Typography>
              <TipoServicoSegment
                lentes={lentesData.lentes}
                value={lenteId}
                onChange={handleLenteChange}
                disabled={lentesLoading}
              />
            </Box>
          )}

          <PeriodoSectionHeader
            inicio={inicio}
            fim={fim}
            lente={activeLente}
          />

          {resumoError && (
            <OperadorFetchErrorAlert
              message="Não foi possível carregar o resumo do período."
              onReload={onReload}
            />
          )}

          <RitmoHero
            resumo={resumo}
            lente={activeLente}
            loading={resumoLoading}
            onKpiClick={handleKpiClick}
            alertSlot={(
              <ErroIntegracaoBanner
                errorCount={resumo.errosIntegracao}
                onVerErros={() => setOpenErrosDrawer(true)}
              />
            )}
            sidePanel={isLenteMtBt(lenteId) ? (
              <AderePeriodoCard
                adere={adere}
                inicio={inicio}
                fim={fim}
                selectedMonth={adereMonth}
                onSelectMonth={handleAdereMonthChange}
                loading={adereLoading && !adere.mesesDisponiveis?.length}
                refreshing={adereLoading}
              />
            ) : null}
          />

          {periodoFetchReady ? (
            <HistoricoPeriodoTable
              apiFilter={historicoApiFilter}
              refreshKey={reload}
              inicio={inicio}
              fim={fim}
              lenteId={lenteId}
              lente={activeLente}
              paginationUrl="/v2/minha-digitacao"
              lenteLabel={historicoSubheaderLente}
              emptyMessage={getHistoricoEmptyMessage(activeLente)}
            />
          ) : (
            <WidgetV2
              title="Histórico do período"
              subheader={`Carregando${formatPeriodoCurto(inicio, fim) ? ` · ${formatPeriodoCurto(inicio, fim)}` : ''}…`}
              initialExpanded
            >
              <LinearProgress sx={{ mx: 2, mb: 2 }} />
            </WidgetV2>
          )}
        </Stack>

        <ErrosIntegracaoQuickDrawer
          open={openErrosDrawer}
          onClose={() => setOpenErrosDrawer(false)}
          apiFilter={periodoApiFilter}
          onReload={onReload}
        />

        <MediaDiariaQuickModal
          open={openMediaModal}
          onClose={() => setOpenMediaModal(false)}
          apiFilter={historicoApiFilter}
          unidade={activeLente?.unidade ?? resumo.unidade ?? 'servicos'}
          metaDiaria={resumo.metaDiaria ?? activeLente?.metaDiaria}
        />

        <DiasUteisCicloQuickModal
          open={openDiasUteisModal}
          onClose={() => setOpenDiasUteisModal(false)}
          inicio={inicio}
          fim={fim}
          diasUteis={resumo.diasUteis}
          diasUteisRestantes={resumo.diasUteisRestantes}
        />

        <ExportFilterDialog
          open={openExportFilter}
          onClose={() => setOpenExportFilter(false)}
          authData={authData}
          onExport={handleConfirmExport}
        />
      </Stack>
    </Box>
  );
};

MinhaDigitacaoV2.propTypes = {
  authData: PropTypes.object.isRequired,
  generateReportDigitadorDiario: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  generateReportDigitadorDiario: actionsNsir.generateReportDigitadorDiario,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MinhaDigitacaoV2));
